import { View } from 'react-native';

import { cn } from '@/components/ui/cn';
import { Surface } from '@/components/ui/Surface';

import { Body } from './Body';
import { Check } from './Check';
import { Display } from './Display';
import { Eyebrow } from './Eyebrow';
import { Heading } from './Heading';
import { Section } from './Section';

const FEATURES = [
  {
    title: 'Build it once',
    body: 'A checklist is a reusable template. Author the steps one time; every run starts clean and identical.',
  },
  {
    title: 'Run in any order',
    body: 'Check items off in whatever order the job actually happens. Nothing is gated; nothing gets skipped by accident.',
  },
  {
    title: 'With your crew',
    body: 'Share a run and everyone checks in real time. Every tick is attributed to a name, so you always know who did what.',
  },
  {
    title: "Sealed when it's done",
    body: 'Close a run and it freezes into an immutable, timestamped log. No edits after the fact, just a record of what happened.',
    ink: true,
  },
];

export function Features() {
  return (
    <Section className="py-20">
      <View className="mb-12 max-w-[680px] gap-4">
        <Eyebrow className="text-xs tracking-[0.14em] text-signal">The pitch</Eyebrow>
        <Display className="text-[38px] leading-[1.08] tracking-[-0.02em] text-foreground">
          Get it done right, every time.
        </Display>
        <Body className="text-lg leading-relaxed text-foreground-secondary">
          Real procedures aren&apos;t tidy. They get done out of order, by more than one
          person, sometimes with no signal, and need to be provable afterward.
        </Body>
      </View>

      <View className="flex-col gap-[18px] md:flex-row md:flex-wrap">
        {FEATURES.map((f) => (
          <Surface
            key={f.title}
            variant={f.ink ? 'ink' : 'card'}
            className="w-full rounded-[20px] p-7 md:w-auto md:min-w-[244px] md:flex-1"
          >
            <View
              className={cn(
                'mb-5 h-12 w-12 items-center justify-center rounded-xl',
                f.ink ? 'bg-signal' : 'border border-border bg-background',
              )}
            >
              {f.ink ? (
                <Check size="md" />
              ) : (
                <View className="h-2.5 w-2.5 rounded-full bg-signal" />
              )}
            </View>
            <Heading
              className={cn(
                'mb-2 text-[19px] tracking-[-0.01em]',
                f.ink ? 'text-on-ink' : 'text-foreground',
              )}
            >
              {f.title}
            </Heading>
            <Body
              className={cn(
                'text-[14.5px] leading-[1.6]',
                f.ink ? 'text-faint' : 'text-foreground-secondary',
              )}
            >
              {f.body}
            </Body>
          </Surface>
        ))}
      </View>

      {/* offline highlight — the positive "no signal" banner (StatusBanner palette) */}
      <View className="mt-[18px] flex-row flex-wrap items-center gap-x-8 gap-y-4 rounded-[20px] border border-positive-border bg-positive-bg px-9 py-8">
        <View className="h-11 w-11 items-center justify-center rounded-xl bg-positive">
          <View className="h-[11px] w-[11px] rounded-full bg-positive-bg" />
        </View>
        <View className="min-w-[240px] flex-1">
          <Heading className="mb-1.5 text-[19px] text-positive">
            Works with no signal
          </Heading>
          <Body className="text-[14.5px] leading-[1.55] text-foreground-secondary">
            Out at the far end of the ramp with no bars? Keep working. Changes save on the
            device and sync the moment you reconnect.
          </Body>
        </View>
      </View>
    </Section>
  );
}
